/**
 * SEO long-form guide articles. Unlike teaser routes, guide slugs are
 * fully localized (e.g. PL `poradnik/...`, DE `ratgeber/...`, EN
 * `guide/...`) so this file is the single source of truth for:
 *   1. The 5 React components that render each guide.
 *   2. App.tsx route registration (one path per language per guide).
 *   3. The vite-react-ssg `includedRoutes` prerender list.
 *   4. The sitemap generator.
 *
 * Logical path equivalence between languages is handled via `id`, not via
 * a shared trailing path - so slugs can differ per language.
 */
import type { Language } from "@/contexts/LanguageContext";

export type GuideId =
  | "how-hive-scale-works"
  | "apiary-monitoring"
  | "beekeeper-app"
  | "hive-weight"
  | "iot-in-beekeeping";

export type GuideSlugs = { en: string; pl: string; de: string };

/**
 * Per-guide slug map. Slugs do NOT include a leading slash - they are
 * appended to the language prefix at routing time.
 */
export const GUIDES: ReadonlyArray<{ id: GuideId; slugs: GuideSlugs }> = [
  {
    id: "how-hive-scale-works",
    slugs: {
      en: "guide/how-hive-scale-works",
      pl: "poradnik/jak-dziala-waga-pasieczna",
      de: "ratgeber/wie-funktioniert-bienenstockwaage",
    },
  },
  {
    id: "apiary-monitoring",
    slugs: {
      en: "guide/apiary-monitoring",
      pl: "poradnik/monitoring-pasieki",
      de: "ratgeber/imkerei-monitoring",
    },
  },
  {
    id: "beekeeper-app",
    slugs: {
      en: "guide/app-for-beekeepers",
      pl: "poradnik/aplikacja-dla-pszczelarzy",
      de: "ratgeber/app-fuer-imker",
    },
  },
  {
    id: "hive-weight",
    slugs: {
      en: "guide/how-much-does-a-hive-weigh",
      pl: "poradnik/ile-wazy-ul-z-miodem",
      de: "ratgeber/wie-viel-wiegt-ein-bienenstock",
    },
  },
  {
    id: "iot-in-beekeeping",
    slugs: {
      en: "guide/iot-in-beekeeping",
      pl: "poradnik/iot-w-pszczelarstwie",
      de: "ratgeber/iot-in-der-imkerei",
    },
  },
] as const;

/**
 * Returns the absolute path (with leading slash) for the given guide in
 * the given language. EN lives at site root, PL/DE under `/pl` and `/de`.
 */
export const guidePathFor = (id: GuideId, lang: Language): string => {
  const guide = GUIDES.find((g) => g.id === id);
  if (!guide) return "/";
  const slug = guide.slugs[lang];
  return lang === "en" ? `/${slug}` : `/${lang}/${slug}`;
};

/**
 * Returns the full hreflang alternates map for a guide, suitable for
 * passing to `<SEO hreflangAlternates={...} />`. Each value is a path
 * (no origin), e.g. `/pl/poradnik/jak-dziala-waga-pasieczna`.
 */
export const guideHreflangAlternates = (id: GuideId): GuideSlugs => ({
  en: guidePathFor(id, "en"),
  pl: guidePathFor(id, "pl"),
  de: guidePathFor(id, "de"),
});

/**
 * Flat list of all 15 prerender paths (5 guides × 3 languages).
 * Used by `vite.config.ts` `includedRoutes` and the sitemap script.
 */
export const GUIDE_PRERENDER_PATHS: string[] = GUIDES.flatMap((g) => [
  guidePathFor(g.id, "en"),
  guidePathFor(g.id, "pl"),
  guidePathFor(g.id, "de"),
]);
